import { getSnapshot } from 'mobx-state-tree';
import React, { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import Box from 'ui-box';
import {
  Button,
  Card,
  Container,
  defaultColors,
  FormField,
  Grid,
  Heading,
  InputGroup,
  majorScale,
  PageContainer,
  useToast,
} from '../../npaper';
import { AddressFormControls } from '../../npaper/AddressFormControls';
import { FileInput } from '../../npaper/FileInput';
import { IOrganisationSnapshotIn, useStores } from '../../stores';
import validateEmail from '../../utils/email-validator';

export const AccountPage = () => {
  const history = useHistory();
  const methods = useForm<IOrganisationSnapshotIn>();
  const { register, handleSubmit, errors, formState, reset } = methods;

  const { authStore, organisationStore } = useStores();
  const [logo, setLogo] = useState<File>();
  const [icon, setIcon] = useState<File>();

  const toaster = useToast();
  const intl = useIntl();

  useEffect(() => {
    if (authStore.currentOrganisation) {
      const org = organisationStore.itemMap.get(
        authStore.currentOrganisation.id + ''
      );
      reset(getSnapshot(org!));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authStore]);

  const onSubmit = async (values: IOrganisationSnapshotIn) => {
    // TODO: Handle icon and logo
    try {
      await organisationStore.updateItem(
        authStore.currentOrganisation!,
        values
      );
      if (logo || icon) {
        await organisationStore.updateLogoAndIcon(
          authStore.currentOrganisation!,
          logo,
          icon
        );
      }
      toaster.addToast({
        message: intl.formatMessage({
          defaultMessage: 'Account details was successfully saved',
        }),
        intent: 'success',
      });

      history.length > 0 ? history.goBack() : history.push('/settings');
    } catch (err) {
      // TODO: Handle error
      console.log(err);
      toaster.showDefaultError(err);
    }
  };

  return (
    <PageContainer>
      <Container paddingBottom={majorScale(8)}>
        <Button
          iconLeft="arrow-left"
          marginBottom={majorScale(2)}
          onClick={() =>
            history.length > 0 ? history.goBack() : history.push('/settings')
          }
        >
          <FormattedMessage defaultMessage="Settings" />
        </Button>
        <FormProvider {...methods}>
          <Grid>
            <Card className="col-span-12 md:col-span-12 lg:col-span-10 xl:col-span-8">
              <div className="dialog">
                <Box className="header">
                  <Heading size={400}>
                    <FormattedMessage defaultMessage="Account details" />
                  </Heading>
                </Box>
                <Grid padding={majorScale(2)}>
                  <Heading size={300} className="col-span-12">
                    <FormattedMessage defaultMessage="Account" />
                  </Heading>

                  <FormField
                    className="col-span-9"
                    label={intl.formatMessage({
                      defaultMessage: 'Account name',
                      description: 'Label',
                    })}
                    inline={true}
                  >
                    <InputGroup
                      placeholder={intl.formatMessage({
                        defaultMessage: 'Business name',
                      })}
                      name="name"
                      isInvalid={!!errors.name}
                      validationMessage={
                        errors.name
                          ? errors.name.message
                          : intl.formatMessage({
                              defaultMessage:
                                'Your account name is not visible to customers',
                              description: 'Account name hint',
                            })
                      }
                      ref={register({
                        required: intl.formatMessage({
                          defaultMessage:
                            'You must provide a name for your account',
                          description: 'Validation: Account name required',
                        }),
                      })}
                    />
                  </FormField>

                  <FormField
                    className="col-span-9"
                    label={intl.formatMessage({
                      defaultMessage: 'Logo',
                    })}
                    inline
                    center
                  >
                    <FileInput
                      placeholder={intl.formatMessage({
                        defaultMessage: 'Upload logo',
                        description: 'Logo file input placeholder',
                      })}
                      thumbnail={
                        authStore.currentOrganisation!.logo
                          ? authStore.currentOrganisation!.logo.small
                          : null
                      }
                      onSelectFile={(file) => {
                        setLogo(file);
                      }}
                    />
                  </FormField>

                  <FormField
                    className="col-span-9"
                    label={intl.formatMessage({
                      defaultMessage: 'Icon',
                    })}
                    inline
                    center
                  >
                    <FileInput
                      placeholder={intl.formatMessage({
                        defaultMessage: 'Upload icon',
                        description: 'Icon file input placeholder',
                      })}
                      thumbnail={
                        authStore.currentOrganisation!.icon
                          ? authStore.currentOrganisation!.icon.small
                          : null
                      }
                      onSelectFile={(file) => {
                        setIcon(file);
                      }}
                    />
                  </FormField>
                </Grid>
                <Grid
                  borderTop={`2px solid ${defaultColors.gray[100]}`}
                  padding={majorScale(2)}
                >
                  <Heading size={300} className="col-span-12">
                    <FormattedMessage
                      defaultMessage="Your company"
                      description="Heading for section with info of your company"
                    />
                  </Heading>

                  <FormField
                    className="col-span-9"
                    label={intl.formatMessage({
                      defaultMessage: 'Legal business name',
                    })}
                    inline={true}
                  >
                    <InputGroup
                      placeholder={intl.formatMessage({
                        defaultMessage: 'Business name',
                      })}
                      name="legal_name"
                      isInvalid={!!errors.legal_name}
                      validationMessage={
                        errors.legal_name
                          ? errors.legal_name.message
                          : intl.formatMessage({
                              defaultMessage:
                                'The legal name is used on invoices and offers and is visible to customers.',
                              description: 'Legal business name hint',
                            })
                      }
                      ref={register({
                        required: intl.formatMessage({
                          defaultMessage:
                            'You must provide the legal name of your business or company.',
                          description:
                            'Validation: Legal business name required',
                        }),
                      })}
                    />
                  </FormField>

                  <FormField
                    className="col-span-9"
                    label={intl.formatMessage({
                      defaultMessage: 'Address',
                    })}
                    inline={true}
                  >
                    <AddressFormControls name="address" />
                  </FormField>

                  <FormField
                    className="col-span-9"
                    label={intl.formatMessage({
                      defaultMessage: 'VAT number',
                    })}
                    inline={true}
                  >
                    <InputGroup
                      placeholder={intl.formatMessage({
                        defaultMessage: 'DK12345678',
                        description: 'Vat number placeholder',
                      })}
                      name="vat_number"
                      ref={register({
                        required: intl.formatMessage({
                          defaultMessage: 'You must provide a valid VAT number',
                          description: 'Validation: Vat number required',
                        }),
                      })}
                      isInvalid={!!errors.vat_number}
                      validationMessage={
                        errors.vat_number
                          ? errors.vat_number.message
                          : intl.formatMessage({
                              defaultMessage:
                                'Provide your EU VAT number and include your country code.',
                              description: 'Vat number hint',
                            })
                      }
                    />
                  </FormField>

                  <FormField
                    className="col-span-9"
                    label={intl.formatMessage({
                      defaultMessage: 'Email',
                    })}
                    inline
                  >
                    <InputGroup
                      placeholder={intl.formatMessage({
                        defaultMessage: 'Email',
                      })}
                      name="email"
                      isInvalid={!!errors.email}
                      validationMessage={errors.email && errors.email.message}
                      ref={register({
                        validate: (v: string) =>
                          (!validateEmail(v) &&
                            intl.formatMessage({
                              defaultMessage:
                                'Please provide a valid email address',
                            })) ||
                          true,
                        required: intl.formatMessage({
                          defaultMessage: 'You must provide an email',
                          description: 'Validation: Email required',
                        }),
                      })}
                    />
                  </FormField>

                  <FormField
                    className="col-span-9"
                    label={intl.formatMessage({
                      defaultMessage: 'Phone',
                    })}
                    inline
                  >
                    <InputGroup
                      placeholder={intl.formatMessage({
                        defaultMessage: 'Phone',
                      })}
                      name="phone"
                      isInvalid={!!errors.email}
                      validationMessage={errors.email && errors.email.message}
                      ref={register({
                        required: intl.formatMessage({
                          defaultMessage: 'You must provide a phone number',
                          description: 'Validation: Phone required',
                        }),
                      })}
                    />
                  </FormField>
                </Grid>
                <Box className="actions">
                  <Button
                    disabled={formState.isSubmitting}
                    onClick={() =>
                      history.length > 0
                        ? history.goBack()
                        : history.push('/settings')
                    }
                  >
                    <FormattedMessage
                      id="defaults.cancel"
                      defaultMessage="Cancel"
                    />
                  </Button>
                  <Button
                    primary
                    isLoading={formState.isSubmitting}
                    onClick={handleSubmit(onSubmit)}
                  >
                    <FormattedMessage
                      id="defaults.save-changes"
                      defaultMessage="Save changes"
                    />
                  </Button>
                </Box>
              </div>
            </Card>
          </Grid>
        </FormProvider>
      </Container>
    </PageContainer>
  );
};
